/**
 * Lab Request Service
 * Cereri laborator — request_id din plajă configurată per org.
 */

import { db } from '../firebase';
import {
  collection, doc, addDoc, updateDoc, getDoc, getDocs,
  query, where, orderBy, serverTimestamp, runTransaction,
  limit as limitQuery
} from 'firebase/firestore';

const COLLECTION = 'lab_requests';
const SEQ_COLLECTION = 'org_sequences';

async function getNextLabRequestId(organizationId) {
  const seqRef = doc(db, SEQ_COLLECTION, organizationId);
  return await runTransaction(db, async (transaction) => {
    const seqSnap = await transaction.get(seqRef);
    const currentValue = seqSnap.exists() ? (seqSnap.data().lab_next_request_id || 1) : 1;
    const nextValue = currentValue + 1;
    if (seqSnap.exists()) {
      transaction.update(seqRef, { lab_next_request_id: nextValue, updated_at: serverTimestamp() });
    } else {
      transaction.set(seqRef, {
        organization_id: organizationId,
        atp_next_id: 1,
        lab_next_request_id: nextValue,
        updated_at: serverTimestamp()
      });
    }
    return currentValue;
  });
}

async function dispatchLabResultPositiveEvent(organizationId, labDocId, requestId, data, userId) {
  try {
    await addDoc(collection(db, 'workflow_events'), {
      event_type: 'LAB_RESULT_POSITIVE',
      organization_id: organizationId,
      entity_type: 'room',
      entity_id: data.room_id,
      event_data: {
        lab_request_id: labDocId,
        request_id: requestId,
        room_id: data.room_id,
        department_id: data.department_id,
        sample_type: data.sample_type,
        test_method: data.test_method,
        result_value: data.result_value,
        interpretation: 'neconform'
      },
      source: 'lab_request',
      created_at: serverTimestamp(),
      created_by: userId,
      processed: false
    });
  } catch (err) {
    console.warn('[labRequestService] dispatchLabResultPositiveEvent failed (non-blocking):', err?.message);
  }
}

export async function createLabRequest(organizationId, data, userId) {
  const requestId = await getNextLabRequestId(organizationId);

  const docData = {
    request_id: requestId,
    organization_id: organizationId,

    requested_at: serverTimestamp(),
    result_recorded_at: null,

    department_id: data.department_id || null,
    department_name: data.department_name || '',
    room_id: data.room_id || null,
    room_name: data.room_name || '',
    surface_type_id: data.surface_type_id || null,
    surface_type_name: data.surface_type_name || null,

    sample_type: data.sample_type || 'suprafete',

    test_method: data.test_method || 'cultura',
    test_method_description: data.test_method_description || '',
    investigation_id: null,

    operator_id: data.operator_id || userId,
    operator_name: data.operator_name || '',

    status: 'pending',

    result_value: null,
    result_interpretation: null,
    result_notes: null,

    workflow_execution_id: data.workflow_execution_id || null,

    // Trasabilitate sursă — de unde vine cererea de probă
    // SECURITY NOTE: source și source_id sunt scrise de server, nu de client
    source: data.source || 'planned_ipc', // 'planned_ipc' | 'audit' | 'direct_observation' | 'incident' | 'capa' | 'risk_map'
    source_id: data.source_id || null,    // ID obiect sursă: audit_id, capa_id, incident_id
    source_ref: data.source_ref || null,  // Referință human-readable: 'AUD-2025-001', 'CAPA-003'

    created_by: userId,
    updated_at: serverTimestamp(),
    updated_by: userId
  };

  const docRef = await addDoc(collection(db, COLLECTION), docData);
  return { id: docRef.id, request_id: requestId };
}

export async function recordLabResult(organizationId, labDocId, resultData, userId) {
  const labRef = doc(db, COLLECTION, labDocId);
  const beforeSnap = await getDoc(labRef);
  if (!beforeSnap.exists()) {
    throw new Error('Cerere inexistentă');
  }
  const existing = beforeSnap.data();
  if (existing.organization_id !== organizationId) {
    throw new Error('Organizație invalidă');
  }

  await updateDoc(labRef, {
    result_value: resultData.result_value || '',
    result_interpretation: resultData.result_interpretation || null,
    result_notes: resultData.result_notes || '',
    status: 'result_received',
    result_recorded_at: serverTimestamp(),
    updated_at: serverTimestamp(),
    updated_by: userId
  });

  if (resultData.result_interpretation === 'neconform') {
    await dispatchLabResultPositiveEvent(
      organizationId,
      labDocId,
      existing.request_id,
      {
        room_id: existing.room_id,
        department_id: existing.department_id,
        sample_type: existing.sample_type,
        test_method: existing.test_method,
        result_value: resultData.result_value || ''
      },
      userId
    );
  }
}

export async function getLabRequests(organizationId, filters = {}) {
  let q = query(
    collection(db, COLLECTION),
    where('organization_id', '==', organizationId),
    orderBy('requested_at', 'desc')
  );
  if (filters.limit) {
    q = query(q, limitQuery(filters.limit));
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export const labRequestService = {
  createLabRequest,
  recordLabResult,
  getLabRequests
};

export default labRequestService;
